import styles from "./ParkingSessionItem.module.scss";
import Icon from "../Icon";
import ProgressBar from "../ProgressBar/ProgressBar";
import { Link, useNavigate } from "react-router";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import useBuyFormStore from "../../stores/useBuyFormStore";
import useVehiclesState from "../../model/useVehiclesState";
import useParkingSession from "../../model/useParkingSession";
import { getTicket } from "../../model/helpers/Session";
import { calculateRemainingParkingTime } from "../../services/parkingTimeService";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { IParkingSession } from "types/vehiclePicker.type";
import { useSecondTicker } from "../../hooks/useSecondTicker";
import { ParkingSessionService } from "../../services/parkingSessionService";

type Props = {
    session: IParkingSession;
};

const ParkingSessionItem = React.memo<Props>(({ session }) => {
    const licencePlate = session.ticket.licensePlate;
    const ticketGUID = session.ticket.ticketGUID;
    const parkingFrom = session.from;
    const parkingTo = session.to;

    const navigate = useNavigate();
    const { t } = useTranslation();

    const { getByIndexAsync: getVehicleByIndexAsync } = useVehiclesState();
    const currentTime = useSecondTicker();

    const [carPlate, setCarPlate] = useState(null);

    useEffect(() => {
        getVehicleByIndexAsync("spz", licencePlate).then((vehicle) => {
            if (vehicle) {
                setCarPlate(vehicle.name);
            }
        });
    }, []);

    const [ticketData, setTicketData] = useState(null);
    const [featureCode, setFeatureCode] = useState(null);
    const [featureType, setFeatureType] = useState(null);
    const [timeLeft, setTimeLeft] = useState<string | null>(null);
    const { setLicencePlate, setEmail } = useBuyFormStore();
    const { updateSession } = useParkingSession();

    useEffect(() => {
        getTicket(ticketGUID, (data: any) => {
            setTicketData(data);
            if (data.sectionCode !== null) {
                setFeatureCode(data.sectionCode);
                setFeatureType("pz");
            } else {
                setFeatureCode(data.parkMachineCode);
                setFeatureType("pm");
            }
        });
    }, [ticketGUID]);

    useEffect(() => {
        if (ticketData?.parkingFrom && ticketData?.parkingTo) {
            const result = calculateRemainingParkingTime(ticketData.parkingTo, t("progressBarComponent.end"));

            if (result.isExpired) {
                const isExpiredMoreThan10Min = ParkingSessionService.isSessionExpiredMoreThan(session, 10);

                if (isExpiredMoreThan10Min && session.isShown !== false) {
                    updateSession({
                        ...session,
                        isShown: false,
                    });
                }
            }

            setTimeLeft(result.timeLeftText);
        }
    }, [ticketData, currentTime, t, session, updateSession]);

    const orderDate = useMemo(() => DateTime.fromISO(parkingFrom).toJSDate(), [parkingFrom]);
    const endDate = useMemo(() => DateTime.fromISO(parkingTo).toJSDate(), [parkingTo]);
    const parkingToDateTime = useMemo(() => DateTime.fromISO(ticketData?.parkingTo), [ticketData?.parkingTo]);

    const handleItemClick = useCallback(() => {
        navigate(`/paid/${ticketGUID}`);
    }, [navigate, ticketGUID]);

    const handleExtendClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setLicencePlate(ticketData.licensePlate);
        if (session.email) setEmail(session.email);
        else setEmail("");
    }, [ticketData, session.email, setLicencePlate, setEmail]);

    return (
        <div className={styles.item} onClick={handleItemClick}>
            <div className={styles.info}>
                <div className={styles.icon}>
                    <Icon.Car size={22} color={"#1D4F8B"} />
                </div>
                <div className={styles.title}>
                    <div className={styles.remaining}>{timeLeft}</div>
                    <div className={styles.parkingInfo}>{carPlate ?? licencePlate}</div>
                </div>
                {featureType && featureCode && ticketData?.licensePlate && (
                    <div className={styles.actions}>
                        <Link
                            to={`/buy/${featureType}/${featureCode}`}
                            className={`${styles.buyButton}`}
                            onClick={handleExtendClick}
                        >
                            {parkingToDateTime > DateTime.now()
                                ? t("home.parkingSessionItem.extend")
                                : t("home.parkingSessionItem.renew")}
                        </Link>
                    </div>
                )}
                <div className={styles.openIcon}>
                    <Icon.CaretRight size={16} color={"#1D4F8B"} />
                </div>
            </div>
            <ProgressBar onlyBar={true} orderDate={orderDate} endDate={endDate}></ProgressBar>
        </div>
    );
});

ParkingSessionItem.displayName = "ParkingSessionItem";

export default ParkingSessionItem;
